/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import { i18n } from './viewXlsMacroI18n.ts';

type HtmlSheetViewerProps = {
	htmlContent: string;
	sheetNames: string[];
	width: string;
	height: string;
	attachmentName: string;
};

const DEFAULT_SHEET_NAME = 'Sheet1';

/**
 * Parses the sheet HTML content for a given sheet name from the full HTML artifact.
 * Matches elements with data-sheet-name attribute.
 */
const getSheetHtml = (htmlContent: string, sheetName: string): string => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(htmlContent, 'text/html');
	const sheetEl = Array.from(doc.querySelectorAll('[data-sheet-name]')).find(
		(el) => el.getAttribute('data-sheet-name') === sheetName,
	);

	if (sheetEl) {
		return `<!doctype html><html><head>${doc.head.innerHTML}</head><body>${sheetEl.outerHTML}</body></html>`;
	}

	if (sheetName === DEFAULT_SHEET_NAME) {
		return htmlContent;
	}

	return '';
};

/**
 * Parses all sheet names from the HTML artifact.
 * Matches legacy ConverterMacro.getSheetNames() / Jsoup behaviour.
 */
export const parseSheetNames = (htmlContent: string): string[] => {
	if (!htmlContent.trim()) {
		return [];
	}

	const parser = new DOMParser();
	const doc = parser.parseFromString(htmlContent, 'text/html');
	const elements = doc.querySelectorAll('[data-sheet-name]');
	const names: string[] = [];

	elements.forEach((el) => {
		const name = el.getAttribute('data-sheet-name');
		if (name) {
			names.push(name);
		}
	});

	return names.length > 0 ? names : [DEFAULT_SHEET_NAME];
};

const styles = cssMap({
	viewer: {
		display: 'flex',
		flexDirection: 'column',
	},
	iframeContainer: {
		position: 'relative',
		overflow: 'hidden',
	},
	tabBar: {
		display: 'flex',
		width: 'max-content',
		minWidth: '100%',
		flexWrap: 'nowrap',
	},
	tabButton: {
		boxSizing: 'border-box',
		backgroundColor: token('color.background.neutral'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		color: token('color.text.subtle'),
		font: token('font.body'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
		cursor: 'pointer',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		height: '32px',
		minWidth: '52px',
		transition: token('motion.listitem.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.input.hovered'),
			color: token('color.text.success'),
		},
	},
	activeTabButton: {
		boxSizing: 'border-box',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('color.background.input'),
		borderBlockEnd: `${token('border.width.selected')} solid ${token('color.border.success')}`,
		color: token('color.text.success'),
		cursor: 'pointer',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		font: token('font.heading.xxsmall'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
		height: '32px',
		minWidth: '52px',
		transition: token('motion.listitem.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.input.hovered'),
			color: token('color.text.success'),
		},
	},
	scroller: {
		width: '100%',
		overflowX: 'auto',
		overflowY: 'hidden',
	},
	iframe: {
		height: '100%',
		width: '100%',
		border: 'none',
		display: 'block',
		backgroundColor: token('elevation.surface'),
	},
});

/**
 * Renders an Excel sheet as an HTML iframe with a tab bar for sheet navigation.
 * Matches the legacy html_viewer.vm template behaviour.
 */
export const HtmlSheetViewer = ({
	htmlContent,
	sheetNames,
	width,
	height,
	attachmentName,
}: HtmlSheetViewerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [activeSheet, setActiveSheet] = useState<string>(sheetNames[0] ?? '');
	const viewerId = useId();
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const tabPanelId = `${viewerId}-sheet-panel`;
	const getTabId = useCallback((index: number) => `${viewerId}-sheet-tab-${index}`, [viewerId]);

	const updateIframe = useCallback(
		(sheetName: string) => {
			const iframe = iframeRef.current;
			if (!iframe) {
				return;
			}
			const sheetHtml = getSheetHtml(htmlContent, sheetName);
			iframe.srcdoc = sheetHtml;
		},
		[htmlContent],
	);

	useEffect(() => {
		if (sheetNames.length > 0) {
			const firstSheetName = sheetNames[0];
			setActiveSheet(firstSheetName);
			updateIframe(firstSheetName);
		}
	}, [sheetNames, updateIframe]);

	const handleTabClick = useCallback(
		(sheetName: string) => {
			setActiveSheet(sheetName);
			updateIframe(sheetName);
		},
		[updateIframe],
	);

	const onTabClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			const sheetName = event.currentTarget.getAttribute('data-sheet-name');
			if (!sheetName) {
				return;
			}
			handleTabClick(sheetName);
		},
		[handleTabClick],
	);
	const activeSheetIndex = sheetNames.indexOf(activeSheet);
	const activeTabId = activeSheetIndex >= 0 ? getTabId(activeSheetIndex) : undefined;

	return (
		<div css={styles.viewer} data-testid="viewxls-html-viewer">
			<div
				css={styles.iframeContainer}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic values from macro params cannot be expressed as static CSS
				style={{ height, width }}
				data-attachment-name={attachmentName}
			>
				<iframe
					ref={iframeRef}
					css={styles.iframe}
					data-testid="viewxls-html-viewer-iframe"
					data-attachment-name={attachmentName}
					id={tabPanelId}
					role="tabpanel"
					aria-labelledby={activeTabId}
					tabIndex={0}
					sandbox="allow-same-origin"
					scrolling="auto"
					title={formatMessage(i18n.spreadsheetIframeTitle, { filename: attachmentName })}
				/>
			</div>
			<div css={styles.scroller} data-testid="viewxls-html-viewer-scroller">
				<div css={styles.tabBar} data-testid="viewxls-html-viewer-tab-bar" role="tablist">
					{sheetNames.map((sheetName, index) => (
						<Pressable
							key={sheetName}
							role="tab"
							id={getTabId(index)}
							xcss={sheetName === activeSheet ? styles.activeTabButton : styles.tabButton}
							testId={`viewxls-tab-${sheetName}`}
							data-sheet-name={sheetName}
							aria-controls={tabPanelId}
							aria-selected={sheetName === activeSheet}
							onClick={onTabClick}
							type="button"
						>
							{sheetName}
						</Pressable>
					))}
				</div>
			</div>
		</div>
	);
};
